import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { api, formatTL, currentMonthKey, shiftMonth, monthLabel, MONTH_NAMES } from "../lib/api";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import YearlyReport from "../components/YearlyReport";

const PIE_COLORS = ["#F59E0B", "#10B981", "#EF4444", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

export default function ReportsPage() {
  const [month, setMonth] = useState(currentMonthKey());
  const [catType, setCatType] = useState("expense");
  const [monthly, setMonthly] = useState([]);
  const [catData, setCatData] = useState([]);
  const [downloading, setDownloading] = useState("");

  const download = async (format) => {
    setDownloading(format);
    try {
      const res = await api.get(`/reports/export?month=${month}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `usta-hesap-rapor-${month}.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(format === "excel" ? "Excel raporu indirildi" : "PDF raporu indirildi");
    } catch {
      toast.error("Rapor indirilemedi");
    } finally {
      setDownloading("");
    }
  };

  const load = useCallback(async () => {
    const [m, c] = await Promise.all([
      api.get("/reports/monthly?months=6"),
      api.get(`/reports/categories?month=${month}&type=${catType}`),
    ]);
    setMonthly(m.data);
    setCatData(c.data);
  }, [month, catType]);

  useEffect(() => { load(); }, [load]);

  const chartData = monthly.map((m) => ({ ...m, net: m.income - m.expense, name: MONTH_NAMES[parseInt(m.month.split("-")[1], 10) - 1].slice(0, 3) }));
  const total = catData.reduce((s, c) => s + c.total, 0);

  return (
    <div className="space-y-6 fade-in-up" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Raporlar</h1>
        <div className="flex items-center gap-2">
          <Button data-testid="download-pdf-button" variant="outline" onClick={() => download("pdf")}
            disabled={!!downloading} className="rounded-full border-border hover:bg-secondary">
            {downloading === "pdf" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            PDF İndir ({monthLabel(month)})
          </Button>
          <Button data-testid="download-excel-button" variant="outline" onClick={() => download("excel")}
            disabled={!!downloading} className="rounded-full border-border hover:bg-secondary">
            {downloading === "excel" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Excel İndir
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6">
        <h2 className="font-heading text-lg font-semibold mb-4">Aylık Gelir / Gider Karşılaştırması</h2>
        <div className="h-72" data-testid="monthly-report-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} barGap={4}>
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                cursor={{ fill: "rgba(51,65,85,0.3)" }}
                contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 12, color: "#F8FAFC" }}
                formatter={(v, n) => [formatTL(v), n === "income" ? "Gelir" : n === "expense" ? "Gider" : "Net Kar/Zarar"]}
              />
              <Legend formatter={(v) => (v === "income" ? "Gelir" : v === "expense" ? "Gider" : "Net Kar/Zarar")} />
              <Bar dataKey="income" fill="#10B981" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expense" fill="#EF4444" radius={[6, 6, 0, 0]} />
              <Line dataKey="net" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3, fill: "#F59E0B" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h2 className="font-heading text-lg font-semibold">Kategori Dağılımı</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-secondary/50 border border-border rounded-full">
              <button data-testid="report-month-prev" onClick={() => setMonth(shiftMonth(month, -1))}
                className="p-2 hover:text-primary transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium px-2 whitespace-nowrap">{monthLabel(month)}</span>
              <button data-testid="report-month-next" onClick={() => setMonth(shiftMonth(month, 1))}
                className="p-2 hover:text-primary transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <Tabs value={catType} onValueChange={setCatType}>
              <TabsList className="bg-secondary">
                <TabsTrigger value="expense" data-testid="category-report-expense-tab">Gider</TabsTrigger>
                <TabsTrigger value="income" data-testid="category-report-income-tab">Gelir</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {catData.length === 0 ? (
          <p data-testid="no-category-data-message" className="text-sm text-muted-foreground py-12 text-center">
            Bu ay için veri bulunamadı.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
            <div className="h-64" data-testid="category-pie-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={catData} dataKey="total" nameKey="category" innerRadius={60} outerRadius={95} paddingAngle={3}>
                    {catData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 12, color: "#F8FAFC" }}
                    formatter={(v) => formatTL(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3" data-testid="category-breakdown-list">
              {catData.map((c, i) => (
                <div key={c.category} className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-sm flex-1 truncate">{c.category}</span>
                  <span className="text-sm font-semibold">{formatTL(c.total)}</span>
                  <span className="text-xs text-muted-foreground w-12 text-right">%{((c.total / total) * 100).toFixed(0)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <YearlyReport />
    </div>
  );
}
